<template>
  <div>
    <div
      v-if="showModal"
      class="modal fade show d-block template-looks-modal"
      tabindex="-1"
      role="dialog"
      style="background: rgba(0, 0, 0, 0.7);"
    >
      <div class="modal-dialog template-looks-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-chart-line"></i> Set Template Looks
            </h5>
            <button type="button" class="close" aria-label="Close" @click="closeModal">
              <span>&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <p class="hint">
              Template looks are copied when experiments don't provide a source look
              link for a folder. Leave a field empty to clear the template.
            </p>
            <div v-if="errors.length" class="errors">
              <div v-for="error in errors" :key="error">{{ error }}</div>
            </div>
            <div
              v-for="(folderConfig, folderId) in folders"
              :key="`template-look-${folderId}`"
              class="template-look-row"
            >
              <label :for="`templateLook-${folderId}`" class="folder-name">{{ folderLabel(folderId, folderConfig) }}</label>
              <input
                class="form-control"
                :id="`templateLook-${folderId}`"
                placeholder="Template Looker link to copy from (optional)"
                v-model="templateLinks[folderId]"
              />
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" :disabled="saving" @click="save">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { managerService } from '../services/manager.service';
import { isValidLookerLink } from '../util/lookerLinkHelper';
import notify from '../util/notification';

export default {
  data() {
    return {
      showModal: false,
      templateLinks: {},
      errors: [],
      saving: false
    };
  },
  computed: {
    folders() {
      return this.$store.getters.lookerConfiguration.folders || {};
    },
    currentTemplateLooks() {
      const templateLooks = {};
      Object.entries(this.folders).forEach(([folderId, folderConfig]) => {
        templateLooks[folderId] = folderConfig?.template_look_link || '';
      });
      return templateLooks;
    }
  },
  methods: {
    openModal() {
      const links = {};
      Object.keys(this.folders).forEach(folderId => {
        links[folderId] = this.currentTemplateLooks[folderId] || '';
      });
      this.templateLinks = links;
      this.errors = [];
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async save() {
      this.errors = [];
      const templates = {};
      Object.entries(this.templateLinks).forEach(([folderId, link]) => {
        const trimmed = typeof link === 'string' ? link.trim() : '';
        if (trimmed && !isValidLookerLink(trimmed)) {
          this.errors.push(`${this.folderLabel(folderId, this.folders[folderId])}: must be a valid Looker look URL`);
        }
        templates[folderId] = trimmed; // empty string clears the template
      });
      if (this.errors.length) return;

      this.saving = true;
      try {
        await managerService.setTemplateLooks({ templates });
        await this.$store.dispatch('fetchLookerConfiguration');
        notify('Template looks saved successfully!', 'success');
        this.showModal = false;
      } catch (err) {
        const errorMessage = err.response?.data?.errorMessage || err.message || 'Unknown error';
        notify(`Template looks couldn't be saved. ${errorMessage}`, 'error');
      } finally {
        this.saving = false;
      }
    },
    folderLabel(folderId, folderConfig) {
      return folderConfig && typeof folderConfig === 'object' && folderConfig.folder_name
        ? folderConfig.folder_name
        : folderId;
    }
  }
};
</script>

<style scoped>
.template-looks-modal {
  color: #1f2937;
}

.template-looks-dialog {
  max-width: 560px;
  margin: 1.75rem auto;
}

.modal-content {
  max-height: calc(100vh - 3.5rem);
  display: flex;
  overflow: hidden;
  background-color: #fff;
  color: #1f2937;
  border: 1px solid #d9e2ec;
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.28);
}

.modal-header {
  flex: 0 0 auto;
  background-color: #f8fafc;
  color: #1f2937;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #d9e2ec;
  padding: 0.85rem 1.25rem;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  color: #1677c8;
  font-size: 1.1rem;
  font-weight: 700;
}

.close {
  color: #334155;
  opacity: 0.8;
  text-shadow: none;
}

.close:hover {
  color: #0f172a;
  opacity: 1;
}

.modal-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 1.15rem 1.25rem 1.25rem;
}

.modal-footer {
  flex: 0 0 auto;
  background-color: #f8fafc;
  border-top: 1px solid #d9e2ec;
  padding: 0.85rem 1.25rem;
}

.hint {
  margin-bottom: 1.1rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.55;
}

.errors {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 10px;
  font-size: 0.85rem;
}

.template-look-row {
  padding: 0;
  margin-bottom: 1rem;
}

.template-look-row:last-child {
  margin-bottom: 0;
}

.folder-name {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 0.4rem;
  color: #1677c8;
  display: block;
}

.form-control {
  height: 38px;
  color: #1f2937 !important;
  background-color: #fff !important;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

.form-control::placeholder {
  color: #64748b;
  opacity: 1;
}

.form-control:focus {
  color: #1f2937 !important;
  background-color: #fff !important;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.16);
}

.btn {
  border-radius: 6px;
  font-weight: 600;
}
</style>
